/* eslint-disable react-refresh/only-export-components */
import { createContextConfig } from '@/domain/mitarbeiter/features/anlageassistent/context/utils';
import {
  AnlageassistentContextType,
  Step,
} from '@/domain/mitarbeiter/features/anlageassistent/types/types';
import { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

const AnlageassistentContext = createContext<
  {
    state: AnlageassistentContextType['state'];
    setState: React.Dispatch<
      React.SetStateAction<AnlageassistentContextType['state']>
    >;
  } & AnlageassistentContextType['contextHelpers']
>({
  validateStepReady: () => true,
  navigateToStep: () => {},
  next: () => {},
  previous: () => {},
  hasNext: () => false,
  hasPrevious: () => false,
  currentStepProgress: () => 0,

  state: {
    currentStepIndex: 0,
    currentSubStepIndex: 0,
    steps: [],
  },

  setState: () => {},
});

// eslint-disable-next-line max-lines-per-function
function AnlageassistentContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [anlageassistentState, setAnlageassistentState] = useState<
    AnlageassistentContextType['state']
  >(createContextConfig({ t }));

  function validateStepReady(step: Step): boolean {
    if (step.type === 'summary') {
      return false;
    }
    return step.subSteps.every(() => {
      return false;
    });
  }

  function navigateToStep(stepIndex: number) {
    setAnlageassistentState((prev) => ({
      ...prev,
      currentStepIndex: stepIndex,
      currentSubStepIndex: 0,
    }));
  }

  function hasNextStep(): boolean {
    return (
      anlageassistentState.currentStepIndex <
      anlageassistentState.steps.length - 1
    );
  }

  function hasNextSubStep(): boolean {
    const currentStep =
      anlageassistentState.steps[anlageassistentState.currentStepIndex];
    if (currentStep.type === 'summary') {
      return false;
    }
    return (
      anlageassistentState.currentSubStepIndex < currentStep.subSteps.length - 1
    );
  }

  function hasPreviousStep(): boolean {
    return anlageassistentState.currentStepIndex > 0;
  }

  function hasPreviousSubStep(): boolean {
    return anlageassistentState.currentSubStepIndex > 0;
  }

  function next() {
    if (hasNextSubStep()) {
      setAnlageassistentState((prev) => ({
        ...prev,
        currentSubStepIndex: prev.currentSubStepIndex + 1,
      }));
    } else if (hasNextStep()) {
      setAnlageassistentState((prev) => ({
        ...prev,
        currentStepIndex: prev.currentStepIndex + 1,
        currentSubStepIndex: 0,
      }));
    }
  }
  function previous() {
    if (hasPreviousSubStep()) {
      setAnlageassistentState((prev) => ({
        ...prev,
        currentSubStepIndex: prev.currentSubStepIndex - 1,
      }));
    } else if (hasPreviousStep()) {
      setAnlageassistentState((prev) => {
        const previousStep = prev.steps[prev.currentStepIndex - 1];
        return {
          ...prev,
          currentStepIndex: prev.currentStepIndex - 1,
          currentSubStepIndex:
            previousStep.type === 'summary'
              ? 0
              : previousStep.subSteps.length > 0
                ? previousStep.subSteps.length - 1
                : 0,
        };
      });
    }
  }

  function currentStepProgress(): number | null {
    const currentStep =
      anlageassistentState.steps[anlageassistentState.currentStepIndex];
    if (currentStep.type === 'summary') {
      return null;
    }
    return (
      ((anlageassistentState.currentSubStepIndex + 1) /
        (currentStep.subSteps.length > 0 ? currentStep.subSteps.length : 1)) *
      100
    );
  }

  return (
    <AnlageassistentContext.Provider
      value={{
        state: anlageassistentState,
        setState: setAnlageassistentState,
        navigateToStep,
        validateStepReady,
        next,
        previous,
        hasNext: hasNextStep,
        hasPrevious: () => hasPreviousStep() || hasPreviousSubStep(),
        currentStepProgress,
      }}>
      {children}
    </AnlageassistentContext.Provider>
  );
}

function useAnlageassistentContext() {
  const context = useContext(AnlageassistentContext);

  if (!context) {
    throw new Error(
      'useAnlageassistentContext must be used within an AnlageassistentContextProvider',
    );
  }

  return {
    ctx: context,
  };
}

export { AnlageassistentContextProvider, useAnlageassistentContext };
